function escapeHtml(value = '') {
  return String(value)
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;')
    .replace(/'/gu, '&#039;');
}

function clean(value = '') {
  return String(value ?? '').trim();
}

function normalizeOptions(values = []) {
  return (Array.isArray(values) ? values : []).map((option) => {
    if (option && typeof option === 'object') {
      return { value: String(option.value ?? ''), label: String(option.label ?? option.value ?? '') };
    }
    return { value: String(option ?? ''), label: String(option ?? '') };
  });
}

function renderAttributeMap(attrs = {}) {
  return Object.entries(attrs && typeof attrs === 'object' ? attrs : {}).map(([key, value]) => {
    const name = clean(key);
    if (!name || value == null || value === false) return '';
    return value === true ? ` ${name}` : ` ${name}="${escapeHtml(String(value))}"`;
  }).join('');
}

function renderColorSwatch(value = '') {
  const normalized = clean(value);
  if (!/^#[0-9a-f]{6}$/iu.test(normalized)) return '';
  return `<svg class="kg-settings-reference-v2-select__swatch" viewBox="0 0 18 18" aria-hidden="true"><circle cx="9" cy="9" r="8" fill="${escapeHtml(normalized)}" /></svg>`;
}

export function createSettingsReferenceV2SelectController(initialOptions = {}) {
  let host = null;
  let open = false;
  let activeIndex = 0;
  let restoreFocus = false;
  let removeOutsideListener = null;
  const options = normalizeOptions(initialOptions.options);
  const disabled = initialOptions.disabled === true;
  const selectAttrs = initialOptions.selectAttrs && typeof initialOptions.selectAttrs === 'object'
    ? { ...initialOptions.selectAttrs }
    : {};
  let value = String(initialOptions.value ?? '');

  function selectedIndex() {
    const index = options.findIndex((option) => option.value === value);
    return index >= 0 ? index : 0;
  }

  function selectedOption() {
    return options.find((option) => option.value === value) || options[0] || { value: '', label: 'Choose an option' };
  }

  function render() {
    if (!host) return;
    const rootId = clean(initialOptions.id) || 'settings-reference-v2-select';
    const triggerId = clean(initialOptions.selectId) || `${rootId}-trigger`;
    const listboxId = `${triggerId}-listbox`;
    const labelId = `${triggerId}-label`;
    const helperId = `${triggerId}-helper`;
    const selected = selectedOption();
    const selectAttributes = Object.fromEntries(Object.entries(selectAttrs).filter(([key]) => key !== 'name'));
    activeIndex = Math.max(0, Math.min(activeIndex, Math.max(0, options.length - 1)));
    host.innerHTML = `
      <div id="${escapeHtml(rootId)}" class="kg-settings-reference-v2-select" data-settings-reference-v2-custom-select data-open="${open ? 'true' : 'false'}">
        <label id="${escapeHtml(labelId)}" class="kg-settings-reference-v2-select__label" for="${escapeHtml(triggerId)}">${escapeHtml(initialOptions.label || '')}</label>
        <select class="kg-settings-reference-v2-select__semantic" name="${escapeHtml(selectAttrs.name || '')}" tabindex="-1" aria-hidden="true"${disabled ? ' disabled aria-disabled="true"' : ''}${renderAttributeMap(selectAttributes)}>
          ${options.map((option) => `<option value="${escapeHtml(option.value)}"${option.value === value ? ' selected' : ''}>${escapeHtml(option.label)}</option>`).join('')}
        </select>
        <button
          id="${escapeHtml(triggerId)}"
          class="kg-settings-reference-v2-select__trigger"
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded="${open ? 'true' : 'false'}"
          aria-controls="${escapeHtml(listboxId)}"
          aria-labelledby="${escapeHtml(labelId)} ${escapeHtml(triggerId)}"
          aria-describedby="${escapeHtml(helperId)}"
          ${selectAttrs.required ? 'aria-required="true"' : ''}
          ${disabled ? 'disabled aria-disabled="true"' : ''}
        >
          <span class="kg-settings-reference-v2-select__value">${renderColorSwatch(selected.value)}<span>${escapeHtml(selected.label)}</span></span><span class="kg-settings-reference-v2-select__chevron" aria-hidden="true"></span>
        </button>
        <div id="${escapeHtml(listboxId)}" class="kg-settings-reference-v2-select__listbox" role="listbox" aria-labelledby="${escapeHtml(labelId)}"${open ? '' : ' hidden'}>
          ${options.map((option, index) => `
            <button
              type="button"
              class="kg-settings-reference-v2-select__option${option.value === value ? ' is-selected' : ''}"
              role="option"
              aria-selected="${option.value === value ? 'true' : 'false'}"
              data-settings-reference-v2-option="${escapeHtml(option.value)}"
              tabindex="${open && index === activeIndex ? '0' : '-1'}"
            ><span class="kg-settings-reference-v2-select__value">${renderColorSwatch(option.value)}<span>${escapeHtml(option.label)}</span></span><span aria-hidden="true"></span></button>
          `).join('')}
        </div>
        <p id="${escapeHtml(helperId)}" class="kg-settings-reference-v2-select__helper">${escapeHtml(initialOptions.helperText || '')}</p>
      </div>
    `;
    bind();
    if (restoreFocus) {
      restoreFocus = false;
      const target = open
        ? host.querySelectorAll('[data-settings-reference-v2-option]')[activeIndex]
        : host.querySelector('.kg-settings-reference-v2-select__trigger');
      target?.focus?.({ preventScroll: true });
    }
  }

  function close({ focusTrigger = false } = {}) {
    if (!open) return;
    open = false;
    restoreFocus = focusTrigger;
    render();
  }

  function choose(nextValue) {
    const next = options.find((option) => option.value === String(nextValue));
    if (!next || disabled) return;
    value = next.value;
    open = false;
    restoreFocus = true;
    render();
    const semanticSelect = host?.querySelector('.kg-settings-reference-v2-select__semantic');
    const view = host?.ownerDocument?.defaultView;
    const EventCtor = view?.Event || globalThis.Event;
    const CustomEventCtor = view?.CustomEvent || globalThis.CustomEvent;
    semanticSelect?.dispatchEvent?.(new EventCtor('input', { bubbles: true }));
    semanticSelect?.dispatchEvent?.(new EventCtor('change', { bubbles: true }));
    if (CustomEventCtor) {
      host?.dispatchEvent?.(new CustomEventCtor('ds:select:change', { bubbles: true, detail: { value } }));
    }
  }

  function move(delta) {
    if (!options.length) return;
    activeIndex = (activeIndex + delta + options.length) % options.length;
    restoreFocus = true;
    render();
  }

  function bind() {
    const trigger = host?.querySelector('.kg-settings-reference-v2-select__trigger');
    trigger?.addEventListener('click', () => {
      if (disabled) return;
      open = !open;
      activeIndex = selectedIndex();
      restoreFocus = true;
      render();
    });
    trigger?.addEventListener('keydown', (event) => {
      if (disabled) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        open = true;
        activeIndex = selectedIndex();
        if (event.key === 'ArrowUp') activeIndex = Math.max(0, options.length - 1);
        restoreFocus = true;
        render();
      }
    });
    host?.querySelectorAll('[data-settings-reference-v2-option]').forEach((option, index) => {
      option.addEventListener('click', () => choose(option.getAttribute('data-settings-reference-v2-option')));
      option.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') { event.preventDefault(); move(1); }
        else if (event.key === 'ArrowUp') { event.preventDefault(); move(-1); }
        else if (event.key === 'Home') { event.preventDefault(); activeIndex = 0; restoreFocus = true; render(); }
        else if (event.key === 'End') { event.preventDefault(); activeIndex = Math.max(0, options.length - 1); restoreFocus = true; render(); }
        else if (event.key === 'Escape') { event.preventDefault(); close({ focusTrigger: true }); }
        else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          choose(options[index]?.value);
        }
        else if (event.key === 'Tab') close();
      });
    });
  }

  return {
    mount(nextHost) {
      host = nextHost && typeof nextHost.querySelector === 'function' ? nextHost : null;
      if (!host) return false;
      render();
      const doc = initialOptions.doc || host.ownerDocument;
      const outside = (event) => {
        if (open && !host?.contains?.(event.target)) close();
      };
      doc?.addEventListener?.('pointerdown', outside);
      removeOutsideListener = () => doc?.removeEventListener?.('pointerdown', outside);
      return true;
    },
    focus() {
      host?.querySelector('.kg-settings-reference-v2-select__trigger')?.focus?.();
    },
    destroy() {
      removeOutsideListener?.();
      removeOutsideListener = null;
      if (host) host.innerHTML = '';
      host = null;
    }
  };
}
